/**
 * User Service
 * Handles all user-related API calls
 */

import apiClient from './api';
import type {
  ApiResponse,
  SafeUser,
  UpdateProfileRequest,
  InviteUserRequest,
  InviteUserResponse,
  UpdateUserRequest,
  ListUsersRequest,
  ListUsersResponse,
} from '../types';
import type { MentionUser } from '../types/comment';

export const userService = {
  /**
   * Get current user profile
   */
  getProfile: async (): Promise<SafeUser> => {
    const response = await apiClient.get<ApiResponse<{ user: SafeUser }>>('/users/me');
    if (response.data.success && response.data.data) {
      // Update user in localStorage
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
      return response.data.data.user;
    }
    throw new Error(response.data.message || 'Failed to fetch profile');
  },

  /**
   * Update current user profile
   */
  updateProfile: async (data: UpdateProfileRequest): Promise<SafeUser> => {
    const response = await apiClient.patch<ApiResponse<{ user: SafeUser }>>('/users/me', data);
    if (response.data.success && response.data.data) {
      // Update user in localStorage
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
      return response.data.data.user;
    }
    throw new Error(response.data.message || 'Failed to update profile');
  },

  /**
   * List all users in tenant
   */
  listUsers: async (params?: ListUsersRequest): Promise<ListUsersResponse> => {
    const response = await apiClient.get<ApiResponse<ListUsersResponse>>('/users', { params });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch users');
  },

  /**
   * Get user by ID
   */
  getUserById: async (id: string): Promise<SafeUser> => {
    const response = await apiClient.get<ApiResponse<{ user: SafeUser }>>(`/users/${id}`);
    if (response.data.success && response.data.data) {
      return response.data.data.user;
    }
    throw new Error(response.data.message || 'Failed to fetch user');
  },

  /**
   * Invite new user
   */
  inviteUser: async (data: InviteUserRequest): Promise<InviteUserResponse> => {
    const response = await apiClient.post<ApiResponse<InviteUserResponse>>('/users/invite', data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to invite user');
  },

  /**
   * Update user
   */
  updateUser: async (id: string, data: UpdateUserRequest): Promise<SafeUser> => {
    const response = await apiClient.patch<ApiResponse<{ user: SafeUser }>>(`/users/${id}`, data);
    if (response.data.success && response.data.data) {
      return response.data.data.user;
    }
    throw new Error(response.data.message || 'Failed to update user');
  },

  /**
   * Deactivate user (active -> inactive)
   */
  deactivateUser: async (id: string): Promise<void> => {
    const response = await apiClient.post<ApiResponse>(`/users/${id}/deactivate`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to deactivate user');
    }
  },

  /**
   * Reactivate user (inactive -> active)
   */
  reactivateUser: async (id: string): Promise<void> => {
    const response = await apiClient.post<ApiResponse>(`/users/${id}/reactivate`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to reactivate user');
    }
  },

  /**
   * Soft delete user (inactive -> hidden). Backend: DELETE /users/:id
   */
  deleteUser: async (id: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse>(`/users/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete user');
    }
  },

  /**
   * Permanently delete a never-joined invited user.
   */
  hardDeleteUser: async (id: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse>(`/users/${id}/permanent`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to permanently delete user');
    }
  },

  /**
   * Restore a soft-deleted user (-> inactive).
   */
  restoreUser: async (id: string): Promise<void> => {
    const response = await apiClient.post<ApiResponse>(`/users/${id}/restore`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to restore user');
    }
  },

  /**
   * Resend invitation email
   */
  resendInvitation: async (id: string): Promise<void> => {
    const response = await apiClient.post<ApiResponse>(`/users/${id}/resend-invitation`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to resend invitation');
    }
  },

  /**
   * Search users for @mention autocomplete
   */
  searchUsersForMention: async (
    query: string,
    limit: number = 10
  ): Promise<MentionUser[]> => {
    const response = await apiClient.get<ApiResponse<{ users: MentionUser[] }>>('/users/search', {
      params: { q: query, limit },
    });
    if (response.data.success && response.data.data) {
      return response.data.data.users;
    }
    throw new Error(response.data.message || 'Failed to search users');
  },

  /**
   * Get all departments
   */
  getDepartments: async (): Promise<Array<{ id: string; name: string; userCount: number }>> => {
    const response = await apiClient.get<
      ApiResponse<{ departments: Array<{ id: string; name: string; userCount: number }> }>
    >('/users/departments');
    if (response.data.success && response.data.data) {
      return response.data.data.departments;
    }
    throw new Error(response.data.message || 'Failed to fetch departments');
  },

  /**
   * Create a department
   */
  createDepartment: async (name: string): Promise<{ name: string; userCount: number }> => {
    const response = await apiClient.post<
      ApiResponse<{ department: { name: string; userCount: number } }>
    >('/users/departments', { name });
    if (response.data.success && response.data.data) {
      return response.data.data.department;
    }
    throw new Error(response.data.message || 'Failed to create department');
  },

  /**
   * Update a department
   */
  updateDepartment: async (
    oldName: string,
    newName: string
  ): Promise<{ name: string; userCount: number }> => {
    const response = await apiClient.patch<
      ApiResponse<{ department: { name: string; userCount: number } }>
    >(`/users/departments/${encodeURIComponent(oldName)}`, { name: newName });
    if (response.data.success && response.data.data) {
      return response.data.data.department;
    }
    throw new Error(response.data.message || 'Failed to update department');
  },

  /**
   * Delete a department
   */
  deleteDepartment: async (name: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse>(
      `/users/departments/${encodeURIComponent(name)}`
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete department');
    }
  },

  /**
   * Change password for authenticated user
   */
  changePassword: async (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<void> => {
    const response = await apiClient.post<ApiResponse>('/auth/change-password', {
      currentPassword,
      newPassword,
      confirmPassword,
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to change password');
    }
  },

  /**
   * Get IDs of current user's direct reports
   * For role-based OKR filtering (managers see their direct reports' OKRs)
   */
  getMyDirectReportIds: async (): Promise<string[]> => {
    const response = await apiClient.get<ApiResponse<{ directReportIds: string[] }>>(
      '/users/me/direct-report-ids'
    );
    if (response.data.success && response.data.data) {
      return response.data.data.directReportIds;
    }
    throw new Error(response.data.message || 'Failed to fetch direct report IDs');
  },
};
